import { useState } from 'react';
import { useSelector } from 'react-redux';
import { FilterListSMSIcon } from '@rsos/assets/icons';
import { Box, Text } from '@rsos/base-components';
import { useCurrentPSAPAccountID } from '@rsos/reduxDataStore/src/features/capstone/psapInfo';
import {
  useGetConversationIsLoading,
  useGetConversation,
  useGetCreateConversation,
  useGetEndConversation,
  useHasConversationButtons,
  useIsConversationAbleToEnd,
  useIsConversationEnded,
} from '@rsos/reduxDataStore/src/features/conversations/utils';
import { StateWithEmergencyCalls } from '@rsos/reduxDataStore/src/features/emergencyCalls/emergencyCallsSlice';
import { useCurrentOrgName } from '@rsos/sinatra/src/selectors';
import useDisplayPhoneNumber from '../../../../hooks/useDisplayPhoneNumber';
import useIsVT911Enabled from '../../../../hooks/useIsVT911Enabled';
import ConditionalFeatureDisplay from '../../../Common/ConditionalFeatureDisplay/ConditionalFeatureDisplay';
import { useChatContext } from '../ChatContext';
import { CollapseButton } from '../SharedChatUI/CollapseButton';
import { TranslationMenu } from '../SharedChatUI/TranslationMenu';
import { TranslationMenuButton } from '../SharedChatUI/TranslationMenuButton';
import { LiveVideoButtonContainer } from '../VideoTo911/LiveVideoButtonContainer';
import {
  HeaderContainer,
  RedCircle,
  StartChatButton,
  StartChatContainer,
  IconButtonsBox,
} from './ConversationsChatbox.styles';
import EndChatModal from './EndChatModal';
import TransferButton from './TransferToAgency/TransferButton';
import TransferToAgencyModal from './TransferToAgency/TransferToAgencyModal';
import { getChatButtonText } from './helpers';

interface ConversationsChatHeaderProps {
  handleStartChat?: () => void;
  isForOverview?: boolean;
  isConversationStarted: boolean;
}

const ConversationsChatHeader = ({
  isConversationStarted,
  handleStartChat,
  isForOverview = false,
}: ConversationsChatHeaderProps) => {
  const { collapse } = useChatContext();
  const isVT911Enabled = useIsVT911Enabled();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isTransferModalOpen, setIsTransferModalOpen] = useState(false);
  const orgName = useCurrentOrgName();

  const callerID = useSelector(
    (state: StateWithEmergencyCalls) => state.emergencyCalls.selectedCallerID,
  );

  const hasButtons = useHasConversationButtons(callerID);
  const isConversationPotentiallyEnded = useIsConversationEnded(callerID);
  const isAbleToEnd = useIsConversationAbleToEnd(callerID);
  const isConversationLoading = useGetConversationIsLoading(callerID);
  const conversation = useGetConversation(callerID);
  const currentUserAuthorityId = useCurrentPSAPAccountID();

  const isConversationEnded =
    isConversationPotentiallyEnded && isConversationStarted;
  const isConversationOngoing =
    !isConversationPotentiallyEnded && isConversationStarted;

  const [fetchCreateConversation, createConversationStates] =
    useGetCreateConversation(callerID);

  const [fetchEndConversation, endConversationStates] =
    useGetEndConversation(callerID);

  const displayPhoneNumber = useDisplayPhoneNumber(callerID);

  const handleStartConversation = async () => {
    if (fetchCreateConversation) {
      return fetchCreateConversation({ callerID, orgName }).then(() => {
        handleStartChat?.();
        collapse?.(false);
        return createConversationStates;
      });
    } else {
      return { isSuccess: false };
    }
  };

  const onStartConversation = async () => {
    if (isConversationOngoing) {
      return { isSuccess: true };
    }

    return handleStartConversation();
  };

  const handleDefaultStartClick = () => {
    if (isConversationOngoing) {
      setIsModalOpen(true);
    } else {
      handleStartConversation();
    }
  };

  const handleModalClose = () => {
    setIsModalOpen(false);
  };

  const handleModalConfirm = () => {
    if (fetchEndConversation) {
      fetchEndConversation({ callerID, orgName });
    }

    setIsModalOpen(false);
  };

  return (
    <HeaderContainer data-name="conversations-chat-header">
      <Box horizontal justify="space-between" width="100%">
        <Box>
          <Text inline fontWeight="bold">
            <Text color="primaryText" size="normal">
              {isConversationOngoing && <RedCircle />}
              {isConversationEnded ? 'Closed Chat' : 'Live Chat'}
            </Text>
            <Box horizontal>
              <Text color="secondaryText" fontWeight="regular" size="xsmall">
                {displayPhoneNumber}
              </Text>
            </Box>
          </Text>
        </Box>
        {isConversationOngoing && !isForOverview && (
          <IconButtonsBox>
            <TranslationMenuButton />
            <CollapseButton />
          </IconButtonsBox>
        )}
      </Box>

      {isConversationOngoing && !isForOverview && <TranslationMenu />}

      {hasButtons && (
        <StartChatContainer gridColumnGap="8px">
          {isVT911Enabled && (
            <Box width="50%">
              <LiveVideoButtonContainer
                onStartConversation={onStartConversation}
              />
            </Box>
          )}
          <ConditionalFeatureDisplay
            conditions={[{ featureFlagEnabled: 'tt911-transfer-to-agency' }]}
            dataName="transfer-to-agency-button"
          >
            {isConversationOngoing && (
              <div style={{ width: '50%' }}>
                <TransferButton onClick={() => setIsTransferModalOpen(true)} />
              </div>
            )}
          </ConditionalFeatureDisplay>
          <Box
            alignItems="center"
            justifyContent="center"
            horizontal
            width={isVT911Enabled ? '50%' : '100%'}
          >
            <StartChatButton
              onClick={handleDefaultStartClick}
              disabled={!isAbleToEnd}
              btnType={isConversationOngoing ? 'warning_tertiary' : 'tertiary'}
              isLoadingProp={
                createConversationStates?.isLoading ||
                endConversationStates?.isLoading ||
                isConversationLoading
              }
            >
              <FilterListSMSIcon width={12} height={12} />
              {getChatButtonText({
                conversation,
                isConversationOngoing,
                currentUserAuthorityId,
              })}
            </StartChatButton>
          </Box>
        </StartChatContainer>
      )}
      <EndChatModal
        isLoading={endConversationStates?.isLoading}
        isOpened={isModalOpen}
        callerID={callerID}
        conversation={conversation}
        onClose={handleModalClose}
        onConfirm={handleModalConfirm}
      />
      <TransferToAgencyModal
        isOpen={isTransferModalOpen}
        onClose={() => setIsTransferModalOpen(false)}
        callerID={callerID}
        orgName={orgName}
      />
    </HeaderContainer>
  );
};

export default ConversationsChatHeader;
